<script setup lang="ts">
import {computed, onMounted, ref} from "vue";
import type {Tag} from "@/Types";
import {InputText, Label} from "@/Components/Library";
import {useTags} from "@/Composables/useTags";

const model = defineModel<Array<Tag>>({default: [], required: true, type: Array});

const props = withDefaults(defineProps<{allowCreating?: boolean; listClass?: string}>(), {
    allowCreating: true,
    listClass: "h-40",
});

const {tags, fetchTags, createTag: createTagFromApi, loading} = useTags();

onMounted(() => {
    void fetchTags();
});

const uniqueTags = computed(() => {
    const allTags = [...tags.value, ...model.value];

    return allTags.filter((tag, index, self) => {
        return self.findIndex((item) => item.name === tag.name) === index;
    });
});

//todo: replace with https://vueuse.org/core/useSorted/
const sortedTags = computed(() => {
    return [...uniqueTags.value].sort((a, b) => {
        if (model.value.some((T) => a.name === T.name) && !model.value.some((T) => b.name === T.name)) {
            return -1;
        }
        if (model.value.some((T) => b.name === T.name) && !model.value.some((T) => a.name === T.name)) {
            return 1;
        }
        return a.name.localeCompare(b.name);
    });
});

const search = ref<string>("");
const filteredTags = computed(() => {
    return sortedTags.value?.filter((T) => T.name.toLowerCase().includes(search.value.toLowerCase()));
});

const toggleTag = (tag: Tag) => {
    if (!model.value.some((T) => T.name === tag.name)) {
        model.value.push(tag);
    } else {
        model.value = model.value.filter((T) => T.name !== tag.name);
    }
    search.value = "";

    //TODO maybe remove this
};

const isSelected = (tag: Tag) => model.value.some((T) => T.name === tag.name);

const createTag = async () => {
    const tagName = search.value.trim();

    if (!tagName || !props.allowCreating) {
        return;
    }
    if (!confirm(`Create tag "${tagName}"?`)) {
        return;
    }
    const tag = await createTagFromApi(tagName);
    if (!model.value.some((T) => T.name === tag.name)) {
        model.value.push(tag);
    }
    search.value = "";
};
</script>
<template>
    <div>
        <Label for="tag-search" class="mb-1 font-bold">Tags</Label>
        <div
            class="relative w-full rounded-lg border border-stone-300 bg-stone-50 focus-within:ring-2 focus-within:ring-askr-green/50 dark:border-stone-600 dark:bg-stone-900"
        >
            <div class="px-2 pt-2">
                <InputText
                    id="tag-search"
                    type="search"
                    v-model="search"
                    placeholder="Start typing to filter tags"
                    fluid
                    enterkeyhint="enter"
                    class="sticky left-0 top-0 z-50 mr-2 w-full focus:bg-white focus:ring-0 dark:focus:bg-stone-950"
                    @keydown.enter.prevent.stop="createTag"
                />
            </div>
            <div
                class="flex flex-col items-center justify-start overflow-y-auto overflow-x-hidden px-3 py-3 text-center"
                :class="listClass"
            >
                <div class="flex flex-wrap items-start justify-center" v-auto-animate>
                    <template v-for="tag in filteredTags" :key="tag.id">
                        <span
                            class="mb-1 mr-1 inline-block cursor-pointer rounded-full border px-2.5 py-1 font-sans leading-none lg:text-xs"
                            @click="toggleTag(tag)"
                            :class="[
                                isSelected(tag)
                                    ? 'border-askr-green bg-askr-green text-white'
                                    : 'border-stone-300 bg-white dark:border-stone-600 dark:bg-stone-900',
                            ]"
                        >
                            {{ tag.name }}
                        </span>
                    </template>
                </div>
                <div v-if="loading" class="py-4 font-bold text-stone-700/60">Loading tags...</div>
                <div
                    v-else-if="!filteredTags.length && search"
                    class="flex flex-col gap-1 py-4 font-bold text-stone-700/60"
                >
                    <span>No matching tags found.</span>
                    <span v-if="props.allowCreating">Press Enter to create a new one.</span>
                </div>
            </div>
        </div>
    </div>
</template>
